import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Users, 
  CheckCircle, 
  Star, 
  Shield, 
  Award, 
  ArrowRight, 
  AlertCircle, 
  RefreshCw, 
  Building2, 
  UserCheck, 
  ExternalLink,
  Sparkles,
  Lock,
  Video,
  BookOpen,
  Play,
  CheckCircle2,
  HelpCircle
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import SEOHead from '../components/SEOHead';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

export default function Webinar() {
  const [formData, setFormData] = useState({
    firstname: '',
    email: '',
    phone: '',
    question: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [webinarData, setWebinarData] = useState({
    host_name: 'Priyanka Karn',
    webinar_date: 'Saturday, March 15th',
    webinar_time: '11:00 AM CST',
    webinar_platform: 'Live on Zoom',
    avatar_image: '/images/priyanka-office.jpg'
  });
  const [loading, setLoading] = useState(true);
  const [showVideoPreview, setShowVideoPreview] = useState(false);
  const { toast } = useToast();

  // Countdown timer state (simulated countdown to next session)
  const [timeLeft, setTimeLeft] = useState({ days: 2, hours: 14, minutes: 35, seconds: 48 });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) return { ...prev, seconds: prev.seconds - 1 };
        if (prev.minutes > 0) return { ...prev, minutes: 59, seconds: 59 };
        if (prev.hours > 0) return { ...prev, hours: prev.hours - 1, minutes: 59, seconds: 59 };
        if (prev.days > 0) return { ...prev, days: prev.days - 1, hours: 23, minutes: 59, seconds: 59 };
        return prev;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch webinar data from Google Sheets API
  const fetchWebinarData = async () => {
    try {
      setLoading(true);
      const response = await api.getWebinarData();
      if (response.ok && response.data) {
        setWebinarData(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch webinar data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWebinarData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await api.webinar({
        ...formData,
        marketing_consent: 'true',
        consentTimestamp: new Date().toISOString()
      });
      
      toast({
        title: "Registration Confirmed!",
        description: "You've reserved your spot. Zoom login details have been dispatched to your email.",
        variant: "success"
      });
      
      setFormData({
        firstname: '',
        email: '',
        phone: '',
        question: ''
      });
    } catch (err) {
      toast({
        title: "Registration Error",
        description: err.message || 'Please try again or contact support.',
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const learningOutcomes = [
    {
      title: "Tax-Free Capital Accumulation (IRC Sec. 7702)",
      desc: "Learn how high earners leverage tax-advantaged insurance structures to compound wealth without IRS drag."
    },
    {
      title: "0% Downside Volatility Risk Protection",
      desc: "Discover fixed index strategies that capture market growth while guaranteeing zero loss during market corrections."
    },
    {
      title: "Living Benefits & Legacy Shielding",
      desc: "Protect your family against severe illness, chronic care expenses, and estate erosion before retirement."
    },
    {
      title: "Income Stream Optimization",
      desc: "Architect a guaranteed lifetime income stream that you can never outlive, unaffected by stock market drops."
    },
    {
      title: "Live Advisory Q&A Session",
      desc: "Direct access to ask specific financial planning, tax reduction, and strategy questions live on Zoom."
    }
  ];

  const leadershipPartners = [
    { name: "Vani Financials", url: "https://www.vanifinancials.com/" },
    { name: "Daas Financials", url: "https://daasfinancials.com/" },
    { name: "Vagish Sinha", url: "https://www.linkedin.com/in/vagishsinha/" },
    { name: "Shafin Sadruddin", url: "https://www.linkedin.com/in/shafin-sadruddin-46339683/" }
  ];

  const faqs = [
    {
      question: "Will a recording be made available?",
      answer: "Yes. All registered attendees receive a limited-time replay link via email within 24 hours of the live session."
    },
    {
      question: "Is this masterclass beginner-friendly?",
      answer: "Absolutely. We break down complex IRS codes, market index mechanics, and policy strategies into clear, actionable frameworks."
    },
    {
      question: "Is there any cost or obligation to register?",
      answer: "No. This masterclass is 100% complimentary. There are no mandatory fees or high-pressure sales pitches."
    },
    {
      question: "How long is the session?",
      answer: "The presentation runs approximately 45 minutes, followed by 15 minutes of live interactive Q&A."
    }
  ];

  return (
    <div className="bg-[#070E1B] text-white min-h-screen font-opensans flex flex-col justify-between">
      <SEOHead 
        title="Exclusive Masterclass: Architecting Generational Wealth & Tax-Free Growth | Grow & Stay Rich"
        description="Join our live masterclass with state-licensed financial professionals. Learn IRC Sec. 7702 tax strategies, zero downside risk wealth preservation, and Living Benefits."
        path="/webinar"
      />
      
      <div>
        <Navigation />

        {/* ── Ambient Radial Background Flares ───────────────── */}
        <div className="relative pt-24 pb-16 overflow-hidden">
          <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
            <div className="absolute top-1/4 -left-32 w-[500px] h-[500px] bg-[#C9A84C]/15 rounded-full blur-[150px] animate-pulse-glow" />
            <div className="absolute top-1/3 -right-32 w-[600px] h-[600px] bg-[#3D6ACC]/20 rounded-full blur-[160px]" />
            <div
              className="absolute inset-0 opacity-[0.06]"
              style={{
                backgroundImage: `radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px)`,
                backgroundSize: '32px 32px'
              }}
            />
          </div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
            
            {/* ── HERO & REGISTRATION CARD GRID ───────────────── */}
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              
              {/* Left Column: Authority Copy & Event Badges */}
              <div className="lg:col-span-7 space-y-6">
                
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs sm:text-sm font-semibold text-[#E8D4A0] tracking-wider uppercase">
                  <Video className="w-4 h-4 text-[#C9A84C]" />
                  Live Masterclass • State-Licensed Financial Advisory
                </div>

                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold font-playfair tracking-tight leading-tight">
                  Architecting <span className="text-gradient-gold">Generational Wealth</span> & Zero-Downside Security
                </h1>
                
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-2xl font-opensans">
                  Learn how successful business leaders and families utilize IRC Sec. 7702 strategies to accumulate wealth tax-free, protect principal against market volatility, and safeguard their legacy.
                </p>

                {/* Interactive Countdown Timer Banner */}
                <div className="bg-[#162236]/90 border border-[#C9A84C]/30 rounded-2xl p-5 backdrop-blur-xl shadow-xl space-y-3">
                  <div className="flex items-center justify-between text-xs text-[#E8D4A0] font-bold uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-[#C9A84C]" />
                      Masterclass Live Countdown
                    </span>
                    <span className="px-2 py-0.5 rounded bg-[#C9A84C]/20 border border-[#C9A84C]/40 text-[10px]">Limited Seats</span>
                  </div>
                  
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div className="bg-[#0D1B2A] border border-[#C9A84C]/20 p-2.5 rounded-xl">
                      <div className="text-xl sm:text-2xl font-bold text-gradient-gold font-playfair">{timeLeft.days}</div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase">Days</div>
                    </div>
                    <div className="bg-[#0D1B2A] border border-[#C9A84C]/20 p-2.5 rounded-xl">
                      <div className="text-xl sm:text-2xl font-bold text-gradient-gold font-playfair">{timeLeft.hours}</div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase">Hours</div>
                    </div>
                    <div className="bg-[#0D1B2A] border border-[#C9A84C]/20 p-2.5 rounded-xl">
                      <div className="text-xl sm:text-2xl font-bold text-gradient-gold font-playfair">{timeLeft.minutes}</div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase">Mins</div>
                    </div>
                    <div className="bg-[#0D1B2A] border border-[#C9A84C]/20 p-2.5 rounded-xl">
                      <div className="text-xl sm:text-2xl font-bold text-gradient-gold font-playfair">{timeLeft.seconds}</div>
                      <div className="text-[10px] text-slate-400 font-semibold uppercase">Secs</div>
                    </div>
                  </div>
                </div>

                {/* Event Time & Format Details */}
                <div className="grid sm:grid-cols-2 gap-4 pt-1">
                  <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl p-4 flex items-center gap-3 backdrop-blur-xl">
                    <div className="p-3 bg-[#162236] border border-[#C9A84C]/30 rounded-xl text-[#C9A84C] shrink-0">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-medium">Live Date & Time</div>
                      <div className="text-xs sm:text-sm font-bold text-white">
                        {loading ? 'Loading...' : `${webinarData.webinar_date} @ ${webinarData.webinar_time}`}
                      </div>
                    </div>
                  </div>

                  <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl p-4 flex items-center gap-3 backdrop-blur-xl">
                    <div className="p-3 bg-[#162236] border border-[#C9A84C]/30 rounded-xl text-[#C9A84C] shrink-0">
                      <Shield className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 font-medium">Session Format</div>
                      <div className="text-xs sm:text-sm font-bold text-white">
                        {webinarData.webinar_platform} (Interactive Q&A)
                      </div>
                    </div>
                  </div>
                </div>

                {/* Executive Mentorship Backing Banner */}
                <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/25 rounded-2xl p-5 backdrop-blur-xl space-y-2">
                  <div className="text-xs font-bold uppercase tracking-wider text-[#E8D4A0] flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-[#C9A84C]" />
                    Backed By Senior Agency Leadership & Advisory Network
                  </div>
                  <div className="flex flex-wrap gap-2 text-xs text-slate-300 pt-1">
                    {leadershipPartners.map((partner, idx) => (
                      <a
                        key={idx}
                        href={partner.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 bg-[#162236] hover:bg-[#1f2d47] px-3 py-1 rounded-full border border-[#C9A84C]/20 text-[#E8D4A0] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                      >
                        <UserCheck className="w-3 h-3 text-[#C9A84C]" />
                        <span>{partner.name}</span>
                        <ExternalLink className="w-2.5 h-2.5 opacity-60" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: High-Conversion Instant Registration Card */}
              <div id="registration-form" className="lg:col-span-5">
                <Card className="bg-[#0D1B2A]/95 border border-[#C9A84C]/40 shadow-2xl rounded-2xl backdrop-blur-xl overflow-hidden">
                  <CardHeader className="bg-[#162236] border-b border-[#C9A84C]/25 p-6 text-center">
                    <div className="inline-flex items-center justify-center gap-1.5 px-3 py-1 rounded-full bg-[#0D1B2A] border border-[#C9A84C]/30 text-xs font-bold text-[#E8D4A0] uppercase mb-2">
                      <Lock className="w-3.5 h-3.5 text-[#C9A84C]" />
                      Reserve Complimentary Seat
                    </div>
                    <CardTitle className="text-xl sm:text-2xl font-bold font-playfair text-white">
                      Register for Masterclass
                    </CardTitle>
                    <CardDescription className="text-xs text-slate-300 mt-1">
                      Instant Zoom access link sent directly to your email
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="p-6 space-y-4">
                    <form onSubmit={handleSubmit} className="space-y-4">
                      
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                          First Name <span className="text-[#C9A84C]">*</span>
                        </label>
                        <input
                          type="text"
                          name="firstname"
                          required
                          value={formData.firstname}
                          onChange={handleInputChange}
                          className="w-full bg-[#162236] border border-[#C9A84C]/30 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#C9A84C] focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                          placeholder="Enter your first name..."
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                          Email Address <span className="text-[#C9A84C]">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full bg-[#162236] border border-[#C9A84C]/30 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#C9A84C] focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                          placeholder="Enter your best email address..."
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                          Phone Number (Optional)
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full bg-[#162236] border border-[#C9A84C]/30 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#C9A84C] focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                          placeholder="(208) 555-0123"
                        />
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-300">
                          Key Question for Live Q&A (Optional)
                        </label>
                        <textarea
                          name="question"
                          rows={2}
                          value={formData.question}
                          onChange={handleInputChange}
                          className="w-full bg-[#162236] border border-[#C9A84C]/30 rounded-xl px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#C9A84C] resize-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                          placeholder="What is your primary financial planning priority?"
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-gradient-gold-cta text-[#070E1B] font-bold py-3.5 text-sm rounded-xl shadow-xl hover:opacity-95 transition-all flex items-center justify-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]"
                      >
                        {submitting ? 'Confirming Spot...' : 'Secure Free Masterclass Spot'}
                        <ArrowRight className="w-4 h-4 text-[#070E1B]" />
                      </Button>

                      <div className="flex items-center justify-center gap-2 text-[10px] text-slate-400 pt-1">
                        <Shield className="w-3.5 h-3.5 text-[#C9A84C]" />
                        <span>100% Confidential • Privacy Protected</span>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* ── KEY LEARNING OUTCOMES SYLLABUS ───────────────── */}
            <div className="space-y-8 pt-6">
              <div className="text-center space-y-2 max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-xs font-semibold text-[#E8D4A0] uppercase">
                  <BookOpen className="w-3.5 h-3.5 text-[#C9A84C]" />
                  Curated Executive Syllabus
                </div>
                <h2 className="text-2xl sm:text-4xl font-bold font-playfair text-white">
                  What You Will Discover in This Masterclass
                </h2>
                <p className="text-xs sm:text-sm text-slate-300">
                  Actionable wealth preservation frameworks engineered by licensed financial professionals.
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {learningOutcomes.map((item, index) => (
                  <div 
                    key={index}
                    className="group bg-[#0D1B2A]/90 border border-[#C9A84C]/25 rounded-2xl p-6 shadow-xl backdrop-blur-xl hover:border-[#C9A84C]/60 hover:-translate-y-1.5 transition-all duration-300 space-y-3"
                  >
                    <div className="w-9 h-9 rounded-xl bg-[#162236] border border-[#C9A84C]/30 flex items-center justify-center text-[#C9A84C] font-bold text-xs group-hover:scale-110 transition-transform">
                      0{index + 1}
                    </div>
                    <h3 className="text-base font-bold font-playfair text-white group-hover:text-[#E8D4A0] transition-colors">
                      {item.title}
                    </h3>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* ── HOST BIOGRAPHY & AUTHORITY SPOTLIGHT ───────────────── */}
            <div className="bg-[#0D1B2A]/90 border border-[#C9A84C]/30 rounded-2xl p-8 shadow-2xl backdrop-blur-xl grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-4 flex flex-col items-center text-center space-y-3">
                <div className="w-28 h-28 rounded-full bg-[#162236] border-2 border-[#C9A84C] flex items-center justify-center text-2xl font-bold font-playfair text-[#E8D4A0] shadow-xl overflow-hidden">
                  <img src="/images/priyanka-office.jpg" alt="Priyanka Karn" className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-playfair text-white">Priyanka Karn</h3>
                  <p className="text-xs text-[#E8D4A0] font-semibold">State-Licensed Life & Financial Advisory Lead</p>
                </div>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#162236] border border-[#C9A84C]/30 text-[10px] text-slate-300">
                  <Award className="w-3.5 h-3.5 text-[#C9A84C]" />
                  <span>Licensed Advisory Lead</span>
                </div>
              </div>

              <div className="lg:col-span-8 space-y-4">
                <h3 className="text-xl font-bold font-playfair text-white">
                  About Your Advisory Host
                </h3>
                <p className="text-xs sm:text-sm text-slate-300 leading-relaxed font-opensans">
                  Priyanka Karn is a state-licensed insurance and annuity specialist serving business leaders and families nationwide. Supported by an elite leadership network including Vani Financials, Daas Financials, Vagish Sinha, and Shafin Sadruddin, she brings top-tier carrier illustrations, Living Benefit underwriting, and tax-free retirement designs to every client consultation.
                </p>
                <div className="grid sm:grid-cols-2 gap-3 pt-1 text-xs">
                  <div className="flex items-center gap-2 bg-[#162236] p-3 rounded-xl border border-[#C9A84C]/20">
                    <CheckCircle2 className="w-4 h-4 text-[#C9A84C] shrink-0" />
                    <span className="text-slate-200">State Licensed & Insured Nationwide</span>
                  </div>
                  <div className="flex items-center gap-2 bg-[#162236] p-3 rounded-xl border border-[#C9A84C]/20">
                    <CheckCircle2 className="w-4 h-4 text-[#C9A84C] shrink-0" />
                    <span className="text-slate-200">IRC Sec. 7702 & IUL Specialist</span>
                  </div>
                </div>
              </div>
            </div>

            {/* ── FREQUENTLY ASKED QUESTIONS ───────────────── */}
            <div className="space-y-6 pt-4">
              <div className="text-center space-y-1">
                <h2 className="text-2xl font-bold font-playfair text-white">Frequently Asked Questions</h2>
                <p className="text-xs text-slate-400">Everything you need to know about joining the masterclass</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
                {faqs.map((faq, idx) => (
                  <div key={idx} className="bg-[#0D1B2A]/90 border border-[#C9A84C]/20 rounded-2xl p-5 shadow-lg space-y-2 hover:border-[#C9A84C]/50 transition-all">
                    <h4 className="text-sm font-bold text-[#E8D4A0] font-playfair flex items-center gap-2">
                      <HelpCircle className="w-3.5 h-3.5 text-[#C9A84C]" />
                      {faq.question}
                    </h4>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
